import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { NgxMaskDirective } from 'ngx-mask';
import { PhoneFormatPipe } from 'src/app/shared/pipes/phone-format.pipe';
import { toNationalDigits } from 'src/app/shared/utils/format.utils';
import { ApiError } from '../../../abstract/api-error';
import { FileUploadService } from '../../../core/api-services/file-upload.service';
import { ChemicalsService } from '../../../core/data-services/chemicals.service';
import { SponsorPointRateService } from '../../../core/data-services/sponsor-point-rate.service';
import { SponsorsService } from '../../../core/data-services/sponsors.service';
import { Sponsor } from '../../../core/data-models/sponsor.model';
import { SponsorPointRate } from '../../../core/data-models/sponsor-point-rate.model';
import { SponsorPointRateCreateRequest } from '../../../core/request-models/sponsor-point-rate-create-request.model';
import type { Chemical } from '../../../core/data-models/chemical.model';
import { environment } from '../../../../environments/environment';
import { SponsorLocationsComponent } from './sponsor-locations/sponsor-locations.component';
import { SponsorRepresentativesComponent } from './sponsor-representatives/sponsor-representatives.component';

/**
 * Sponsor Detail Component
 *
 * Displays sponsor information in a four-tab layout:
 * Info, Locations, Representatives, and Rates.
 * Includes logo upload, brand color, contact type,
 * and point rate configuration.
 */
@Component({
  selector: 'app-sponsor-detail',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    SponsorLocationsComponent,
    SponsorRepresentativesComponent,
    NgxMaskDirective,
    PhoneFormatPipe
  ],
  templateUrl: './sponsor-detail.component.html',
  styleUrl: './sponsor-detail.component.scss'
})
export class SponsorDetailComponent implements OnInit {
  sponsorId = '';
  sponsor: Sponsor | null = null;
  rates: SponsorPointRate[] = [];
  sponsorChemicals: Chemical[] = [];

  loading = true;
  errorMessage = '';
  successMessage = '';

  // Tab navigation
  activeTab: 'info' | 'locations' | 'representatives' | 'rates' = 'info';

  // Brand color editing
  editingBrandColor = false;
  brandColorValue = '';

  // Description editing
  editingDescription = false;
  descriptionValue = '';

  // Contact type editing
  editingContactType = false;
  contactTypeValue = '';

  // Contact message editing
  editingContactMessage = false;
  contactMessageValue = '';

  // Website editing
  editingWebsite = false;
  websiteValue = '';

  // Phone editing
  editingPhone = false;
  phoneValue = '';

  // Email editing
  editingEmail = false;
  emailValue = '';

  // Logo upload state
  uploadingLogo = false;

  // Add rate form
  showAddRateForm = false;
  newRate: SponsorPointRateCreateRequest = {
    chemicalId: '',
    rateValue: 0,
    rateUnit: 'gallon',
    calculationMode: 'per_acre',
    effectiveStart: '',
    effectiveEnd: '',
  };
  addRateError = '';
  addingRate = false;

  // End rate state
  endingRateId: string | null = null;
  endRateDate = '';

  // Available rate units
  rateUnits = [
    'gallon', 'quart', 'pint', 'fluid ounce',
    'liter', 'milliliter',
    'ounce', 'pound', 'gram', 'kilogram'
  ];

  // Available calculation modes
  calculationModes: { value: string; label: string; description: string }[] = [
    { value: 'per_acre', label: 'Per Acre', description: 'Points awarded based on acres treated' },
    { value: 'per_usage', label: 'Per Usage', description: 'Points awarded based on total chemical volume used' }
  ];

  // Contact type options
  contactTypeOptions = [
    { value: 'locations_only', label: 'Locations Only' },
    { value: 'reps_only', label: 'Representatives Only' },
    { value: 'mixed', label: 'Mixed (Locations & Reps)' },
    { value: 'contact_form_only', label: 'Contact Form Only' }
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private sponsorsService: SponsorsService,
    private sponsorPointRateService: SponsorPointRateService,
    private chemicalsService: ChemicalsService,
    private fileUploadService: FileUploadService
  ) {}

  ngOnInit(): void {
    this.sponsorId = this.route.snapshot.params['id'];
    this.loadSponsor();
  }

  /**
   * Load sponsor data and its rates
   */
  loadSponsor(): void {
    this.loading = true;
    this.errorMessage = '';

    this.sponsorsService.getSponsor(this.sponsorId).subscribe({
      next: (result) => {
        if (result) {
          const sponsor = result.sponsor;
          this.sponsor = sponsor;
          this.rates = result.rates;
          this.brandColorValue = sponsor.brandColor || '';
          this.descriptionValue = sponsor.description || '';
          this.contactTypeValue = sponsor.contactType || 'contact_form_only';
          this.contactMessageValue = sponsor.contactMessage || '';
          this.websiteValue = sponsor.website || '';
          this.phoneValue = toNationalDigits(sponsor.phone);
          this.emailValue = sponsor.email || '';
          this.loadSponsorChemicals();
        } else {
          this.errorMessage = 'Sponsor not found';
        }
        this.loading = false;
      },
      error: (err) => {
        console.error('Failed to load sponsor:', err);
        this.errorMessage = 'Failed to load sponsor details';
        this.loading = false;
      }
    });
  }

  /**
   * Load chemicals belonging to this sponsor for the rate form dropdown
   */
  loadSponsorChemicals(): void {
    this.chemicalsService.listChemicals({ sponsor: true, limit: 200 }).subscribe({
      next: (page) => {
        this.sponsorChemicals = page.items.filter(
          c => c.sponsorId === this.sponsorId
        );
      },
      error: (err) => {
        console.error('Failed to load chemicals:', err);
      }
    });
  }

  // ============================================
  // TAB NAVIGATION
  // ============================================

  setActiveTab(tab: 'info' | 'locations' | 'representatives' | 'rates'): void {
    this.activeTab = tab;
  }

  // ============================================
  // BRAND COLOR
  // ============================================

  startEditBrandColor(): void {
    this.editingBrandColor = true;
    this.brandColorValue = this.sponsor?.brandColor || '';
  }

  cancelEditBrandColor(): void {
    this.editingBrandColor = false;
    this.brandColorValue = this.sponsor?.brandColor || '';
  }

  saveBrandColor(): void {
    if (!this.sponsor) return;

    this.sponsorsService.updateSponsor(this.sponsorId, {
      brandColor: this.brandColorValue || null
    }).subscribe({
      next: () => {
        if (this.sponsor) {
          this.sponsor.brandColor = this.brandColorValue || null;
        }
        this.editingBrandColor = false;
        this.showSuccess('Brand color updated');
      },
      error: (err) => {
        console.error('Failed to update brand color:', err);
        this.errorMessage = 'Failed to update brand color';
      }
    });
  }

  // ============================================
  // DESCRIPTION
  // ============================================

  startEditDescription(): void {
    this.editingDescription = true;
    this.descriptionValue = this.sponsor?.description || '';
  }

  cancelEditDescription(): void {
    this.editingDescription = false;
    this.descriptionValue = this.sponsor?.description || '';
  }

  saveDescription(): void {
    if (!this.sponsor) return;

    this.sponsorsService.updateSponsor(this.sponsorId, {
      description: this.descriptionValue || null
    }).subscribe({
      next: () => {
        if (this.sponsor) {
          this.sponsor.description = this.descriptionValue || null;
        }
        this.editingDescription = false;
        this.showSuccess('Description updated');
      },
      error: (err) => {
        console.error('Failed to update description:', err);
        this.errorMessage = 'Failed to update description';
      }
    });
  }

  // ============================================
  // CONTACT TYPE
  // ============================================

  startEditContactType(): void {
    this.editingContactType = true;
    this.contactTypeValue = this.sponsor?.contactType || 'contact_form_only';
  }

  cancelEditContactType(): void {
    this.editingContactType = false;
    this.contactTypeValue = this.sponsor?.contactType || 'contact_form_only';
  }

  saveContactType(): void {
    if (!this.sponsor) return;

    this.sponsorsService.updateSponsor(this.sponsorId, {
      contactType: this.contactTypeValue
    }).subscribe({
      next: () => {
        if (this.sponsor) {
          this.sponsor.contactType = this.contactTypeValue;
        }
        this.editingContactType = false;
        this.showSuccess('Contact type updated');
      },
      error: (err) => {
        console.error('Failed to update contact type:', err);
        this.errorMessage = 'Failed to update contact type';
      }
    });
  }

  // ============================================
  // CONTACT MESSAGE
  // ============================================

  startEditContactMessage(): void {
    this.editingContactMessage = true;
    this.contactMessageValue = this.sponsor?.contactMessage || '';
  }

  cancelEditContactMessage(): void {
    this.editingContactMessage = false;
    this.contactMessageValue = this.sponsor?.contactMessage || '';
  }

  saveContactMessage(): void {
    if (!this.sponsor) return;

    this.sponsorsService.updateSponsor(this.sponsorId, {
      contactMessage: this.contactMessageValue || null
    }).subscribe({
      next: () => {
        if (this.sponsor) {
          this.sponsor.contactMessage = this.contactMessageValue || null;
        }
        this.editingContactMessage = false;
        this.showSuccess('Contact message updated');
      },
      error: (err) => {
        console.error('Failed to update contact message:', err);
        this.errorMessage = 'Failed to update contact message';
      }
    });
  }

  // ============================================
  // WEBSITE
  // ============================================

  startEditWebsite(): void {
    this.editingWebsite = true;
    this.websiteValue = this.sponsor?.website || '';
  }

  cancelEditWebsite(): void {
    this.editingWebsite = false;
    this.websiteValue = this.sponsor?.website || '';
  }

  saveWebsite(): void {
    if (!this.sponsor) return;

    this.sponsorsService.updateSponsor(this.sponsorId, {
      website: this.websiteValue || null
    }).subscribe({
      next: () => {
        if (this.sponsor) {
          this.sponsor.website = this.websiteValue || null;
        }
        this.editingWebsite = false;
        this.showSuccess('Website updated');
      },
      error: (err) => {
        console.error('Failed to update website:', err);
        this.errorMessage = 'Failed to update website';
      }
    });
  }

  // ============================================
  // PHONE
  // ============================================

  startEditPhone(): void {
    this.editingPhone = true;
    this.phoneValue = toNationalDigits(this.sponsor?.phone);
  }

  cancelEditPhone(): void {
    this.editingPhone = false;
    this.phoneValue = toNationalDigits(this.sponsor?.phone);
  }

  savePhone(): void {
    if (!this.sponsor) return;

    this.sponsorsService.updateSponsor(this.sponsorId, {
      phone: this.phoneValue || null
    }).subscribe({
      next: () => {
        if (this.sponsor) {
          this.sponsor.phone = this.phoneValue || null;
        }
        this.editingPhone = false;
        this.showSuccess('Phone updated');
      },
      error: (err) => {
        console.error('Failed to update phone:', err);
        this.errorMessage = 'Failed to update phone';
      }
    });
  }

  // ============================================
  // EMAIL
  // ============================================

  startEditEmail(): void {
    this.editingEmail = true;
    this.emailValue = this.sponsor?.email || '';
  }

  cancelEditEmail(): void {
    this.editingEmail = false;
    this.emailValue = this.sponsor?.email || '';
  }

  saveEmail(): void {
    if (!this.sponsor) return;

    this.sponsorsService.updateSponsor(this.sponsorId, {
      email: this.emailValue || null
    }).subscribe({
      next: () => {
        if (this.sponsor) {
          this.sponsor.email = this.emailValue || null;
        }
        this.editingEmail = false;
        this.showSuccess('Email updated');
      },
      error: (err) => {
        console.error('Failed to update email:', err);
        this.errorMessage = 'Failed to update email';
      }
    });
  }

  /**
   * Returns a human-readable label for a contact type value
   */
  getContactTypeLabel(contactType: string | null | undefined): string {
    const option = this.contactTypeOptions.find(o => o.value === contactType);
    return option?.label || 'Contact Form Only';
  }

  // ============================================
  // LOGO UPLOAD
  // ============================================

  /**
   * Handle logo file selection with flattened RxJS chain.
   * Steps: get pre-signed URL -> upload to S3 -> save URL to sponsor.
   */
  onLogoFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const file = input.files[0];
    const validExts = ['.png', '.jpg', '.jpeg', '.svg'];
    const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

    if (!validExts.includes(ext)) {
      this.errorMessage = 'Invalid file type. Allowed: PNG, JPG, JPEG, SVG';
      return;
    }

    void this.uploadLogo(file);
  }

  /** Pre-signs, PUTs the file to S3 (public-read, as the hub signed it), then saves the URL on the sponsor. */
  private async uploadLogo(file: File): Promise<void> {
    this.uploadingLogo = true;
    this.errorMessage = '';
    try {
      const { uploadUrl, fileUrl, contentType } = await this.fileUploadService.getSponsorLogoUploadUrl(file.name);
      await this.fileUploadService.uploadToSignedUrl(uploadUrl, file, { publicRead: true, contentType });
      await firstValueFrom(this.sponsorsService.updateSponsor(this.sponsorId, { logoUrl: fileUrl }));
      if (this.sponsor) {
        this.sponsor.logoUrl = fileUrl;
      }
      this.showSuccess('Logo uploaded successfully');
    } catch (err) {
      console.error('Failed during logo upload:', err);
      this.errorMessage = 'Failed to upload logo';
    } finally {
      this.uploadingLogo = false;
    }
  }

  // ============================================
  // RATE MANAGEMENT
  // ============================================

  toggleAddRateForm(): void {
    this.showAddRateForm = !this.showAddRateForm;
    this.addRateError = '';
    if (this.showAddRateForm) {
      this.resetNewRateForm();
    }
  }

  resetNewRateForm(): void {
    this.newRate = {
      chemicalId: '',
      rateValue: 0,
      rateUnit: 'gallon',
      calculationMode: 'per_acre',
      effectiveStart: '',
      effectiveEnd: '',
    };
  }

  submitRate(): void {
    this.addRateError = '';

    if (!this.newRate.chemicalId) {
      this.addRateError = 'Please select a chemical';
      return;
    }
    if (!this.newRate.rateValue || this.newRate.rateValue <= 0) {
      this.addRateError = 'Rate value must be greater than 0';
      return;
    }
    if (!this.newRate.effectiveStart) {
      this.addRateError = 'Effective start date is required';
      return;
    }
    if (this.newRate.effectiveEnd && this.newRate.effectiveEnd <= this.newRate.effectiveStart) {
      this.addRateError = 'Effective end date must be after start date';
      return;
    }

    this.addingRate = true;

    const request: SponsorPointRateCreateRequest = {
      chemicalId: this.newRate.chemicalId,
      rateValue: this.newRate.rateValue,
      rateUnit: this.newRate.rateUnit,
      calculationMode: this.newRate.calculationMode,
      effectiveStart: this.newRate.effectiveStart,
    };

    if (this.newRate.effectiveEnd) {
      request.effectiveEnd = this.newRate.effectiveEnd;
    }

    this.sponsorPointRateService.createRate(this.sponsorId, request).subscribe({
      next: () => {
        this.showAddRateForm = false;
        this.addingRate = false;
        this.showSuccess('Rate created successfully');
        this.loadSponsor();
      },
      error: (err) => {
        console.error('Failed to create rate:', err);
        this.addRateError = ApiError.fromHttpError(err).message;
        this.addingRate = false;
      }
    });
  }

  startEndRate(rateId: string): void {
    this.endingRateId = rateId;
    this.endRateDate = new Date().toISOString().split('T')[0];
  }

  cancelEndRate(): void {
    this.endingRateId = null;
    this.endRateDate = '';
  }

  confirmEndRate(): void {
    if (!this.endingRateId || !this.endRateDate) return;

    this.sponsorPointRateService.endRate(this.sponsorId, this.endingRateId, this.endRateDate).subscribe({
      next: () => {
        this.endingRateId = null;
        this.endRateDate = '';
        this.showSuccess('Rate ended successfully');
        this.loadSponsor();
      },
      error: (err) => {
        console.error('Failed to end rate:', err);
        this.errorMessage = ApiError.fromHttpError(err).message;
      }
    });
  }

  // ============================================
  // STATUS HELPERS
  // ============================================

  getRateStatus(rate: SponsorPointRate): 'Active' | 'Expired' | 'Upcoming' {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (!rate.effectiveStart) {
      return 'Active';
    }
    const start = new Date(rate.effectiveStart);
    start.setHours(0, 0, 0, 0);

    if (start > today) {
      return 'Upcoming';
    }

    if (rate.effectiveEnd) {
      const end = new Date(rate.effectiveEnd);
      end.setHours(0, 0, 0, 0);
      if (end < today) {
        return 'Expired';
      }
    }

    return 'Active';
  }

  getStatusBadgeClass(status: string): string {
    switch (status) {
      case 'Active': return 'bg-success';
      case 'Expired': return 'bg-secondary';
      case 'Upcoming': return 'bg-info';
      default: return 'bg-secondary';
    }
  }

  getChemicalName(chemicalId: string | null | undefined): string {
    const chem = this.sponsorChemicals.find(c => c.id === chemicalId);
    return chem?.name || chemicalId || '';
  }

  /**
   * Returns a human-readable label for the calculation mode
   */
  getCalculationModeLabel(mode: string | null | undefined): string {
    switch (mode) {
      case 'per_usage': return 'Per Usage';
      case 'per_acre':
      default:
        return 'Per Acre';
    }
  }

  /**
   * Returns the description for the currently selected calculation mode in the form
   */
  getSelectedCalculationModeDescription(): string {
    const mode = this.calculationModes.find(m => m.value === this.newRate.calculationMode);
    return mode?.description || '';
  }

  // ============================================
  // UTILITY
  // ============================================

  /**
   * Resolve a logo path to a full CDN URL.
   * Handles both relative paths and legacy full URLs.
   */
  getLogoFullUrl(logoUrl: string | null | undefined): string {
    if (!logoUrl) return '';
    if (logoUrl.startsWith('http')) return logoUrl;
    return `${environment.appCDN}/${logoUrl}`;
  }

  formatDate(dateString: string | null | undefined): string {
    if (!dateString) return 'Open-ended';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  showSuccess(message: string): void {
    this.successMessage = message;
    setTimeout(() => {
      this.successMessage = '';
    }, 4000);
  }

  goBack(): void {
    this.router.navigate(['/sponsors']);
  }
}
